import React, { useCallback, useMemo } from 'react';
import {
  Feature,
  OfferType,
  Recurrence,
  ShortOfferDto,
  useCapabilitiesOffers,
  useCurrentOrganizationId,
  useOffers,
  useSubscribeToOfferUrl,
} from '@app/api';
import { useTranslation } from 'react-i18next';
import { Theme } from '@app/theme';
import { Button, CenteredModal, InformationBox } from '@app/core';
import { CheckboxBlockField, CheckboxBlockList } from '@app/form';
import { createUseStyles } from 'react-jss';
import { OfferList } from '../Offers';
import { G_CALENDAR_LINK } from '../GoogleCalendar/gCalendar';

const useStyles = createUseStyles((theme: Theme) => ({
  overlay: {
    zIndex: 10000,
  },
  invoiceBox: {
    marginBottom: theme.marginBase * 2,
  },
  rightButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  feature?: Feature | boolean;
}

export const UpsellSubscriptionModal = ({
  isOpen,
  onRequestClose,
  feature,
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const organizationId = useCurrentOrganizationId(true);
  const [recurrence, setrecurrence] = React.useState<Recurrence>(
    Recurrence.YEARLY,
  );
  const { mutateAsync: getSubscribeCheckoutUrl } = useSubscribeToOfferUrl();

  const { data: allOffers } = useOffers();

  const { data: capabilitiesOffers } = useCapabilitiesOffers();
  const featureCapabilitiesOffers =
    feature && feature !== true && capabilitiesOffers
      ? capabilitiesOffers[feature]
      : undefined;
  const product = featureCapabilitiesOffers?.product;

  const subscriptionsOffers = useMemo(
    () => allOffers?.filter((o) => o.type === OfferType.SUBSCRIPTION) || [],
    [allOffers],
  );

  const onOfferClick = useCallback(
    async (offer: ShortOfferDto) => {
      const { url } = await getSubscribeCheckoutUrl({
        offerId: offer.id,
        recurrence,
      });
      window.open(url, '_blank');
      onRequestClose();
    },
    [getSubscribeCheckoutUrl, recurrence, onRequestClose],
  );

  return (
    <CenteredModal
      title={
        product
          ? t('billing.subscription.upsell.title', product)
          : t('billing.subscription.upsell.titleNoProduct')
      }
      text={product && t('billing.subscription.upsell.text', product)}
      isOpen={isOpen && !!feature && !!organizationId}
      onRequestClose={onRequestClose}
      xlarge
      overlayClassName={classes.overlay}
    >
      <CheckboxBlockList>
        <CheckboxBlockField
          value={Recurrence.MONTHLY}
          label={t('billing.subscription.upsell.monthly')}
          variant="green"
          type="radio"
          onChange={() => setrecurrence(Recurrence.MONTHLY)}
          checked={recurrence === Recurrence.MONTHLY}
        />
        <CheckboxBlockField
          value={Recurrence.YEARLY}
          label={t('billing.subscription.upsell.yearly')}
          variant="green"
          type="radio"
          onChange={() => setrecurrence(Recurrence.YEARLY)}
          checked={recurrence === Recurrence.YEARLY}
        />
      </CheckboxBlockList>

      <InformationBox
        variant="info"
        title={t('billing.subscription.upsell.getInvoice.title')}
        text={t('billing.subscription.upsell.getInvoice.description')}
        className={classes.invoiceBox}
      >
        <div className={classes.rightButton}>
          <Button
            variant="transparent"
            small
            href={G_CALENDAR_LINK}
            target="_blank"
          >
            {t('billing.subscription.upsell.getInvoice.cta')}
          </Button>
        </div>
      </InformationBox>

      <OfferList
        offers={subscriptionsOffers}
        category={product?.category}
        recurrence={recurrence}
        onClick={onOfferClick}
      />
    </CenteredModal>
  );
};
